import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import { getSubjectColorNew, getSubjectIconName } from '../../../../helper';
import { primaryButtonProps } from '../AdminViewUser';

import {
  deleteUserSubjectAsAdmin,
  getUsersState,
} from '../../../../store/UserSlice';
import {
  ICurriculum,
  IGrade,
  ISubject,
} from '../../../../store/V2/SubjectSlice';
import AddSubjectModal from './modals/AddSubjectModal';

const gridTitleProps: TypographyProps = {
  textAlign: 'center',
  padding: '15px 0px',
  borderRadius: '7px',
  sx: {
    color: '#F8FAFC',
    backgroundColor: TP_COLORS.blue.dark,
  },
};

type TState = {
  selectedSubject: ISubject | null;
  selectedCurriculum: ICurriculum | null;
};

const initialState: TState = {
  selectedSubject: null,
  selectedCurriculum: null,
};

const AdminSubjectCurriculum: React.FC = () => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [isAddSubjectModalOpen, setAddSubjectModalOpen] = useState(false);
  const [userSubjects, setUserSubjects] = useState<Array<ISubject>>([]);

  // Store
  const { user: userData } = useAppSelector(getUsersState);

  useEffect(() => {
    let newSubjects: ISubject[] = [];
    let newState: TState = initialState;
    if (userData && userData.subjects?.length) {
      newSubjects = convertSubjectList([...userData.subjects]);
      newState = {
        selectedSubject: newSubjects[0],
        selectedCurriculum: newSubjects[0].curriculums[0],
      };
    }
    setUserSubjects(newSubjects);
    setState(newState);
  }, [userData]);

  const convertSubjectList = (subjects1) => {
    const subjectsMap = new Map();

    subjects1.forEach(({ subject, curriculum, grade, _id }) => {
      if (!subjectsMap.has(subject)) {
        subjectsMap.set(subject, {
          subject,
          curriculums: [],
        });
      }

      const subjectEntry = subjectsMap.get(subject);
      const curriculumEntry = subjectEntry.curriculums.find(
        (c) => c.curriculum === curriculum
      );

      if (curriculumEntry) {
        curriculumEntry.grades.push({ _id, grade });
      } else {
        subjectEntry.curriculums.push({
          curriculum,
          grades: [{ _id, grade }],
        });
      }
    });

    return Array.from(subjectsMap.values());
  };

  const handleSubjectClick = (subjectItem: ISubject) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubject: subjectItem,
      selectedCurriculum: subjectItem.curriculums[0],
    }));
  };

  const handleCurriculumClick = (curriculumItem: ICurriculum) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: curriculumItem,
    }));
  };

  const handleDelete = async (gradeItem: IGrade) => {
    if (gradeItem._id && userData) {
      await dispatch(
        deleteUserSubjectAsAdmin({ userId: userData._id, _id: gradeItem._id })
      );
    }
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          Subject & Curriculum
        </Typography>
        <Button
          {...primaryButtonProps}
          onClick={() => setAddSubjectModalOpen(true)}
        >
          + Add New
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography {...gridTitleProps}>Subjects</Typography>
          <Grid container spacing="10px" marginTop="10px">
            {userSubjects.map((subjectItem, index) => {
              const isSelected =
                state.selectedSubject?.subject === subjectItem.subject;
              const subjectColor = getSubjectColorNew(subjectItem.subject);

              return (
                <Grid item key={index} xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: isSelected ? `${subjectColor}33` : '',
                      borderRadius: '10px',
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleSubjectClick(subjectItem)}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px',
                        }}
                      >
                        <img
                          src={`${
                            process.env.PUBLIC_URL
                          }/images/landingPage/icon-${getSubjectIconName(
                            subjectItem.subject
                          )}.avif`}
                          alt="subject-img"
                          style={{
                            margin: '10px',
                            height: '50px',
                            backgroundColor: `${subjectColor}33`,
                            borderRadius: '30px',
                          }}
                        />

                        <Typography
                          variant="subtitle2"
                          className="ellipsis-text"
                          color={subjectColor}
                        >
                          {subjectItem.subject}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        {/* <Divider
          flexItem
          orientation="vertical"
          sx={{ borderStyle: 'dashed' }}
        /> */}
        <Grid item xs={12} sm={4}>
          <Typography {...gridTitleProps}>Curriculums</Typography>
          <Grid container spacing="10px" marginTop="10px">
            {state.selectedSubject?.curriculums.map((curriculumItem, index) => {
              const isSelected =
                state.selectedCurriculum?.curriculum ===
                curriculumItem.curriculum;

              return (
                <Grid item key={index} xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: isSelected
                        ? `#6474881A`
                        : `${TP_COLORS.border.color2}1A`,
                      borderRadius: '10px',
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCurriculumClick(curriculumItem)}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '20px 10px 20px 20px',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          className="ellipsis-text"
                          color={TP_COLORS.gray.darken}
                        >
                          {curriculumItem.curriculum}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography {...gridTitleProps}>Grades</Typography>
          <Grid container spacing="10px" marginTop="10px">
            {state.selectedCurriculum?.grades.map((gradeItem, index) => (
              <Grid item key={index} xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CardContent
                    sx={{
                      width: '100%',
                      padding: '20px 10px 20px 20px',
                      borderRadius: '10px',
                      backgroundColor: `${TP_COLORS.border.color2}1A`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      className="ellipsis-text"
                      color={TP_COLORS.gray.darken}
                    >
                      {gradeItem.grade}
                    </Typography>
                  </CardContent>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(gradeItem)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      {isAddSubjectModalOpen && (
        <AddSubjectModal
          open={isAddSubjectModalOpen}
          onClose={() => setAddSubjectModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default AdminSubjectCurriculum;
